import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { useAPI } from 'src/util/use-api';
import { useWebsocket, WebsocketMessageType } from 'src/util/use-websocket';
import type { WebsocketListener } from 'src/util/use-websocket';
import { safeGetItem, safeSetItem } from 'src/util/safe-storage';

import type {
  AssistantAction,
  AssistantMessage,
  AssistantQuestion,
  ActionDecision,
  ChatEvent,
} from './types';
import {
  normalizeMessage,
  parseChatEvent,
  resolveActualDecision,
} from './types';
import { reducer, initialState, OPTIMISTIC_SEQ } from './reducer';
import { resolveViewCommand } from './viewCommands';
import { classifyAssistantError, MAX_SELECTED_OPTIONS } from './errors';
import type {
  AssistantErrorCode,
  AssistantErrorOutcome,
  AssistantRequestKind,
} from './errors';

// Per-user, client-side panel state persisted across navigation (same keys the
// design reference uses).
const OPEN_STORAGE_KEY = 'br-assistant-open';
const FULL_STORAGE_KEY = 'br-assistant-full';
const PINNED_STORAGE_KEY = 'br-assistant-pinned';
const WIDTH_STORAGE_KEY = 'br-assistant-width';

const MIN_WIDTH = 360;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 440;
// Pinned mode auto-unpins below this viewport width.
const PIN_MIN_VIEWPORT = 1200;

const viewportWidth = () =>
  typeof window !== 'undefined' ? window.innerWidth : 1440;

/**
 * Clamp the panel width. Overlay: [360, min(720, viewport)]. Pinned: keep the
 * page usable by capping at viewport − 900, i.e. [360, max(360, min(720, vw−900))].
 */
function clampWidth(w: number, pinned: boolean): number {
  const vw = viewportWidth();
  const cap = pinned ? Math.min(MAX_WIDTH, vw - 900) : Math.min(MAX_WIDTH, vw);
  return Math.max(MIN_WIDTH, Math.min(w, Math.max(MIN_WIDTH, cap)));
}

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID)
      return crypto.randomUUID();
  } catch (e) {
    /* noop */
  }
  return (
    'cmid-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

/**
 * Pull ChatMessage serializations out of a REST payload. The SDK types these
 * endpoints as `void`, so the response is untyped and comes in two shapes:
 * a list (GET …/messages/) or `{ user_message, assistant_message? }` (POST 202).
 */
function extractMessages(payload: any): any[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.messages)) return payload.messages;
  return [payload.user_message, payload.assistant_message].filter(Boolean);
}

// ---------------- context ----------------

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean, instant?: boolean) => void;
  listView: boolean;
  showList: (show: boolean) => void;
  conversationId: string | null;
  messages: AssistantMessage[];
  turnActive: boolean;
  connected: boolean;
  reconnecting: boolean;
  banner: { kind: 'warn' | 'ok'; text: string } | null;
  loadingHistory: boolean;
  fullScreen: boolean;
  toggleFullScreen: () => void;
  pinned: boolean;
  togglePinned: () => void;
  width: number;
  setWidth: (w: number) => void;
  resizing: boolean;
  setResizing: (resizing: boolean) => void;
  send: (text: string) => void;
  answerQuestion: (
    q: AssistantQuestion,
    selected: string[],
    echoLabel?: string
  ) => void;
  decideAction: (action: AssistantAction, decision: ActionDecision) => void;
  retryTurn: (message: AssistantMessage) => void;
  newChat: () => void;
  openConversation: (conversationId: string, title?: string) => void;
  conversationsRefreshKey: number;
  /** The backend denied access (403): the assistant is hidden and inert. */
  forbidden: boolean;
  /** Last failed request, shown inline in the panel. */
  error: AssistantErrorCode | null;
  dismissError: () => void;
  reportError: (
    error: unknown,
    kind: AssistantRequestKind
  ) => AssistantErrorOutcome;
  /** Text of a message that failed to send, handed back to the composer. */
  restoredDraft: { text: string; id: number } | null;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export const useAssistant = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx)
    throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
};

export function AssistantProvider({ children }: { children: React.ReactNode }) {
  const value = useProvideAssistant();
  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
}

function useProvideAssistant(): AssistantContextValue {
  const router = useRouter();
  const { API } = useAPI();
  const { registerListener, removeListener, connected, session, websocket } =
    useWebsocket();

  const [open, setOpenState] = useState(false);
  const [listView, setListView] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [banner, setBanner] = useState<{
    kind: 'warn' | 'ok';
    text: string;
  } | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [widthState, setWidthState] = useState(DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);
  const [conversationsRefreshKey, setConversationsRefreshKey] = useState(0);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<AssistantErrorCode | null>(null);
  const [restoredDraft, setRestoredDraft] = useState<{
    text: string;
    id: number;
  } | null>(null);

  const lastSeqRef = useRef(0);
  const conversationIdRef = useRef<string | null>(null);
  const pinnedRef = useRef(false);
  const widthRef = useRef(widthState);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  conversationIdRef.current = conversationId;
  pinnedRef.current = pinned;
  widthRef.current = widthState;

  // Clamp on set so callers can pass a raw pointer-derived width.
  const setWidth = useCallback((w: number) => {
    setWidthState(clampWidth(w, pinnedRef.current));
  }, []);

  // ---- open state persistence ----
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    safeSetItem('localStorage', OPEN_STORAGE_KEY, next ? '1' : '0');
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  const restoreDraft = useCallback((text: string) => {
    setRestoredDraft((prev) => ({ text, id: (prev?.id ?? 0) + 1 }));
  }, []);

  // Map a failed request to its user-facing outcome. A 403 means the user has
  // no assistant access: close and hide it rather than showing an error.
  const reportError = useCallback((e: unknown, kind: AssistantRequestKind) => {
    const outcome = classifyAssistantError(e, kind);
    if (outcome.forbidden) {
      setForbidden(true);
      setOpenState(false);
      setPinned(false);
    }
    setError(outcome.code);
    return outcome;
  }, []);

  useEffect(() => {
    const wasOpen = safeGetItem('localStorage', OPEN_STORAGE_KEY) === '1';
    if (wasOpen) setOpenState(true);
    if (safeGetItem('localStorage', FULL_STORAGE_KEY) === '1')
      setFullScreen(true);
    const storedWidth = Number(safeGetItem('localStorage', WIDTH_STORAGE_KEY));
    // Restore pinned only if the panel was open and the viewport is wide enough.
    const restorePinned =
      safeGetItem('localStorage', PINNED_STORAGE_KEY) === '1' &&
      wasOpen &&
      viewportWidth() >= PIN_MIN_VIEWPORT;
    // Re-clamp: the viewport may be narrower than when the width was saved.
    if (storedWidth >= MIN_WIDTH && storedWidth <= MAX_WIDTH)
      setWidthState(clampWidth(storedWidth, restorePinned));
    if (restorePinned) setPinned(true);
  }, []);

  const persist = (key: string, on: boolean) => {
    safeSetItem('localStorage', key, on ? '1' : '0');
  };

  // Pinned and full screen are mutually exclusive.
  const toggleFullScreen = useCallback(() => {
    setFullScreen((f) => {
      const next = !f;
      persist(FULL_STORAGE_KEY, next);
      if (next && pinnedRef.current) {
        setPinned(false);
        persist(PINNED_STORAGE_KEY, false);
      }
      return next;
    });
  }, []);

  const togglePinned = useCallback(() => {
    setPinned((p) => {
      // Can't pin on a narrow viewport (would leave the page unusable).
      if (!p && viewportWidth() < PIN_MIN_VIEWPORT) return p;
      const next = !p;
      persist(PINNED_STORAGE_KEY, next);
      if (next) {
        setFullScreen(false);
        persist(FULL_STORAGE_KEY, false);
        // Shrink to the pinned clamp if the current width is too wide.
        setWidthState((w) => clampWidth(w, true));
      }
      return next;
    });
  }, []);

  // Persist width when a resize gesture ends.
  useEffect(() => {
    if (!resizing) {
      safeSetItem('localStorage', WIDTH_STORAGE_KEY, String(widthRef.current));
    }
  }, [resizing]);

  // Re-clamp width + auto-unpin on viewport resize while pinned.
  useEffect(() => {
    const onResize = () => {
      if (!pinnedRef.current) return;
      if (viewportWidth() < PIN_MIN_VIEWPORT) {
        setPinned(false);
        persist(PINNED_STORAGE_KEY, false);
      } else {
        setWidthState((w) => clampWidth(w, true));
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Esc closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // ---- REST: fetch full message history (source of truth) ----
  const refetchMessages = useCallback(
    async (convId: string) => {
      if (!API || !convId) return;
      setLoadingHistory(true);
      try {
        const res = await (
          API.assistantAPI as any
        ).assistantConversationsMessagesRetrieve({ conversationId: convId });
        // Conversation may have been switched while this was in flight — don't
        // apply a stale response over the now-active transcript.
        if (convId !== conversationIdRef.current) return;
        const raw = extractMessages(res?.data);
        const msgs = raw.map(normalizeMessage);
        // Reconcile answered questions: each answering user message carries
        // `question_response.{question_message, selected_options}`. Backend's
        // Question object has `answered_by` but not the chosen options, so pull
        // them back onto the question-bearing message for the selected highlight.
        raw.forEach((r) => {
          const qr = r?.question_response;
          if (!qr?.question_message) return;
          const target = msgs.find(
            (m) => m.external_id === qr.question_message
          );
          if (target?.question) {
            target.question = {
              ...target.question,
              selected_options: qr.selected_options || [],
              answered_by:
                target.question.answered_by ?? r.external_id ?? 'user',
            };
          }
        });
        dispatch({ t: 'setMessages', messages: msgs });
        lastSeqRef.current = msgs.reduce((mx, m) => Math.max(mx, m.seq), 0);
        const active = msgs.some(
          (m) => m.status === 'PENDING' || m.status === 'RUNNING'
        );
        dispatch({ t: 'setTurnActive', value: active });
      } catch (e) {
        // Leave existing messages in place on failure.
        if (convId === conversationIdRef.current) reportError(e, 'history');
      } finally {
        setLoadingHistory(false);
      }
    },
    [API, reportError]
  );

  // ---- websocket CHAT_EVENT listener ----
  const applyEvent = useCallback(
    (ev: ChatEvent) => {
      // conversation_activity can target ANY conversation (background updates),
      // so handle it before the active-conversation filter: refresh the list so
      // has_unread / ordering update. If it's the open thread, pull the update.
      if (ev.type === 'conversation_activity') {
        setConversationsRefreshKey((k) => k + 1);
        if (
          ev.conversation_id === conversationIdRef.current &&
          ev.last_seq &&
          ev.last_seq > lastSeqRef.current
        ) {
          refetchMessages(conversationIdRef.current);
        }
        return;
      }

      if (ev.conversation_id !== conversationIdRef.current) return;

      // Gap detection on message-boundary events only (within-message events
      // like deltas/tool_calls share the message seq, so they must not trip it).
      // `truncated` always forces a refetch.
      const boundary =
        ev.type === 'user_message_ack' || ev.type === 'assistant_completed';
      if (
        ev.truncated ||
        (boundary && ev.seq && ev.seq > lastSeqRef.current + 1)
      ) {
        const missed = ev.seq - lastSeqRef.current - 1;
        refetchMessages(conversationIdRef.current);
        setBanner({ kind: 'ok', text: String(Math.max(missed, 1)) });
        if (bannerTimer.current) clearTimeout(bannerTimer.current);
        bannerTimer.current = setTimeout(() => setBanner(null), 2200);
        return;
      }
      if (ev.seq) lastSeqRef.current = Math.max(lastSeqRef.current, ev.seq);

      switch (ev.type) {
        case 'user_message_ack':
          // The user message is already rendered optimistically and reconciled
          // from the POST response; nothing more to do.
          break;
        case 'assistant_delta':
          if (ev.delta != null && ev.message_id)
            dispatch({ t: 'delta', messageId: ev.message_id, delta: ev.delta });
          break;
        case 'assistant_completed':
          dispatch({
            t: 'complete',
            messageId: ev.message_id,
            status: 'COMPLETED',
          });
          dispatch({ t: 'setTurnActive', value: false });
          break;
        case 'error':
          dispatch({
            t: 'fail',
            messageId: ev.message_id,
            error: ev.error_detail,
          });
          dispatch({ t: 'setTurnActive', value: false });
          break;
        case 'tool_call':
          if (ev.tool_call && ev.message_id)
            dispatch({
              t: 'toolCall',
              messageId: ev.message_id,
              toolCall: ev.tool_call,
            });
          break;
        case 'question_asked':
          if (ev.question && ev.message_id)
            dispatch({
              t: 'question',
              messageId: ev.message_id,
              question: ev.question,
            });
          // A question ends the turn (awaits the user's answer).
          dispatch({ t: 'setTurnActive', value: false });
          break;
        case 'action_confirmation_required':
          // No `message` on this event → attach to the current assistant turn.
          if (ev.action)
            dispatch({
              t: 'action',
              messageId: ev.message_id,
              action: ev.action,
            });
          // The card awaits the user's decision — end the streaming turn so no
          // caret/text keeps rendering above it.
          dispatch({ t: 'setTurnActive', value: false });
          break;
        case 'view_command': {
          if (!ev.command) break;
          const resolved = resolveViewCommand(ev.command);
          if (resolved) {
            router.push(resolved.href);
          } else {
            dispatch({
              t: 'viewCommand',
              messageId: ev.message_id,
              command: ev.command,
            });
          }
          break;
        }
        case 'conversation_state':
          // Pushed to every tab on this conversation, not just the one that
          // posted the decision, so a remote confirm/reject/expiry is reflected
          // everywhere instead of leaving a stale actionable card.
          if (ev.action) {
            dispatch({
              t: 'actionDecided',
              actionId: ev.action.external_id,
              decision: ev.action.decision ?? null,
            });
          }
          break;
        default:
          break;
      }
    },
    [refetchMessages, router]
  );

  useEffect(() => {
    if (!registerListener || forbidden) return;
    const listener: WebsocketListener = {
      key: 'assistant-chat-events',
      message_type: WebsocketMessageType.CHAT_EVENT,
      callback: (message) => {
        const ev = parseChatEvent(message.data);
        if (ev) applyEvent(ev);
      },
    };
    registerListener(listener);
    return () => removeListener(listener);
  }, [registerListener, removeListener, applyEvent, forbidden]);

  // ---- reconnect detection: websocket instance changed while open ----
  const wsRef = useRef(websocket);
  useEffect(() => {
    const prev = wsRef.current;
    wsRef.current = websocket;
    if (prev && websocket && prev !== websocket && conversationIdRef.current) {
      // socket was recreated → we may have missed frames while down
      refetchMessages(conversationIdRef.current);
      setBanner({ kind: 'ok', text: '0' });
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
      bannerTimer.current = setTimeout(() => setBanner(null), 2200);
    }
  }, [websocket, refetchMessages]);

  const reconnecting = open && !connected && Boolean(session);

  // ---- posting a message ----
  const postMessage = useCallback(
    async (convId: string, content: string, clientMessageId: string) => {
      dispatch({ t: 'setTurnActive', value: true });
      try {
        const res = await (
          API!.assistantAPI as any
        ).assistantConversationsMessagesCreate(
          { conversationId: convId },
          {
            data: {
              content,
              client_message_id: clientMessageId,
              websocket_session: session?.external_id,
            },
          }
        );
        // Ignore a late response if the user switched conversations meanwhile.
        if (convId !== conversationIdRef.current) return;
        extractMessages(res?.data).forEach((m) =>
          dispatch({ t: 'upsert', message: normalizeMessage(m) })
        );
        setConversationsRefreshKey((k) => k + 1);
      } catch (e) {
        if (convId !== conversationIdRef.current) return;
        // Undo the optimistic bubble and hand the text back to the composer.
        dispatch({ t: 'removeByClientId', clientMessageId });
        dispatch({ t: 'setTurnActive', value: false });
        restoreDraft(content);
        reportError(e, 'message');
      }
    },
    [API, session, restoreDraft, reportError]
  );

  // Cache the in-flight create so two overlapping sends can't each POST a new
  // conversation (check-then-act race → split transcript server-side).
  // conversationGenerationRef bumps on every newChat/openConversation so a
  // creation started for a conversation the user has since switched away
  // from can't activate itself or post the caller's message there.
  const conversationGenerationRef = useRef(0);
  const ensureConversationPromiseRef = useRef<Promise<string | null> | null>(
    null
  );
  const ensureConversationRequestRef = useRef<{ generation: number } | null>(
    null
  );
  const ensureConversation = useCallback(
    async (generation: number): Promise<string | null> => {
      if (generation !== conversationGenerationRef.current) return null;
      if (conversationIdRef.current) return conversationIdRef.current;
      if (!API) return null;
      if (!ensureConversationPromiseRef.current) {
        const request = { generation };
        ensureConversationRequestRef.current = request;
        ensureConversationPromiseRef.current = (async () => {
          try {
            const res = await API.assistantAPI.assistantConversationsCreate({
              conversationRequest: {},
            });
            const id = res.data.external_id;
            if (generation !== conversationGenerationRef.current) return null;
            setConversationId(id);
            conversationIdRef.current = id;
            setConversationsRefreshKey((k) => k + 1);
            return id;
          } catch (e) {
            if (generation === conversationGenerationRef.current) {
              reportError(e, 'message');
            }
            return null;
          } finally {
            if (ensureConversationRequestRef.current === request) {
              ensureConversationPromiseRef.current = null;
              ensureConversationRequestRef.current = null;
            }
          }
        })();
      }
      return ensureConversationPromiseRef.current;
    },
    [API, reportError]
  );

  // Synchronous lock (not React state) so two sends fired in the same tick
  // (e.g. a double-click or double-Enter) can't both read turnActive as
  // false and both reach postMessage - the second one queues instead.
  const sendingRef = useRef(false);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content) return;
      const clientMessageId = uuid();
      setError(null);

      if (state.turnActive || sendingRef.current) {
        // queue it — rendered at reduced opacity
        dispatch({
          t: 'addUser',
          message: {
            external_id: '',
            client_message_id: clientMessageId,
            role: 'USER',
            content,
            seq: OPTIMISTIC_SEQ,
            tool_calls: [],
            queued: true,
            optimistic: true,
          },
        });
        return;
      }

      sendingRef.current = true;
      try {
        // optimistic user bubble
        dispatch({
          t: 'addUser',
          message: {
            external_id: '',
            client_message_id: clientMessageId,
            role: 'USER',
            content,
            seq: OPTIMISTIC_SEQ,
            tool_calls: [],
            optimistic: true,
          },
        });

        const generation = conversationGenerationRef.current;
        const convId = await ensureConversation(generation);
        if (!convId || generation !== conversationGenerationRef.current) {
          dispatch({ t: 'removeByClientId', clientMessageId });
          if (generation === conversationGenerationRef.current) {
            restoreDraft(content);
          }
          return;
        }
        await postMessage(convId, content, clientMessageId);
      } finally {
        sendingRef.current = false;
      }
    },
    [state.turnActive, ensureConversation, postMessage, restoreDraft]
  );

  // ---- drain the queue when a turn finishes ----
  useEffect(() => {
    if (state.turnActive) return;
    const nextQueued = state.messages.find((m) => m.queued);
    if (!nextQueued || !nextQueued.client_message_id) return;
    const convId = conversationIdRef.current;
    if (!convId) return;
    dispatch({
      t: 'markQueuedSent',
      clientMessageId: nextQueued.client_message_id,
    });
    postMessage(convId, nextQueued.content, nextQueued.client_message_id);
  }, [state.turnActive, state.messages, postMessage]);

  // ---- structured question answer ----
  const answerQuestion = useCallback(
    async (q: AssistantQuestion, selected: string[], echoLabel?: string) => {
      const convId = conversationIdRef.current;
      if (!convId || !API) return;
      if (selected.length > MAX_SELECTED_OPTIONS) {
        setError('too_many_options');
        return;
      }
      setError(null);
      dispatch({
        t: 'questionAnswered',
        messageId: q.question_message,
        selected,
      });
      const echoId = uuid();
      if (echoLabel) {
        dispatch({
          t: 'addUser',
          message: {
            external_id: '',
            client_message_id: echoId,
            role: 'USER',
            content: echoLabel,
            seq: OPTIMISTIC_SEQ,
            tool_calls: [],
            optimistic: true,
          },
        });
      }
      dispatch({ t: 'setTurnActive', value: true });
      try {
        await (API.assistantAPI as any).assistantConversationsMessagesCreate(
          { conversationId: convId },
          {
            data: {
              client_message_id: uuid(),
              websocket_session: session?.external_id,
              question_response: {
                question_message: q.question_message,
                selected_options: selected,
              },
            },
          }
        );
      } catch (e) {
        if (convId !== conversationIdRef.current) return;
        dispatch({ t: 'setTurnActive', value: false });
        const outcome = reportError(e, 'question');
        if (outcome.refetch) {
          refetchMessages(convId);
        } else {
          // Re-open the question and drop the echoed answer.
          dispatch({
            t: 'question',
            messageId: q.question_message,
            question: q,
          });
          dispatch({ t: 'removeByClientId', clientMessageId: echoId });
        }
      }
    },
    [API, session, reportError, refetchMessages]
  );

  // ---- action confirm / reject ----
  const decideAction = useCallback(
    async (action: AssistantAction, decision: ActionDecision) => {
      if (!API) return;
      const convId = conversationIdRef.current;
      setError(null);
      dispatch({ t: 'actionDecided', actionId: action.external_id, decision });
      try {
        const res = await (API.assistantAPI as any).assistantActionsCreate(
          { actionId: action.external_id },
          {
            data: { decision },
          }
        );
        // The request can be rejected server-side (e.g. already decided,
        // expired) while still returning 200 — apply the action's actual
        // resulting status rather than trusting the decision we asked for.
        const actualDecision = resolveActualDecision(
          res?.data?.status,
          decision
        );
        if (actualDecision !== decision) {
          dispatch({
            t: 'actionDecided',
            actionId: action.external_id,
            decision: actualDecision,
          });
        }
      } catch (e) {
        // Ignore a late failure for a conversation the user has since
        // switched away from — it must not report an error or refetch
        // messages into whatever conversation is now open.
        if (convId !== conversationIdRef.current) return;
        const outcome = reportError(e, 'action');
        if (outcome.refetch && convId) {
          refetchMessages(convId);
        } else {
          dispatch({
            t: 'actionDecided',
            actionId: action.external_id,
            decision: action.decision ?? null,
          });
        }
      }
    },
    [API, reportError, refetchMessages]
  );

  // ---- retry a failed turn ----
  const retryTurn = useCallback(
    (message: AssistantMessage) => {
      // Find the preceding user message content and re-post it.
      const idx = state.messages.findIndex(
        (m) => m.external_id === message.external_id
      );
      let content = '';
      for (let i = idx - 1; i >= 0; i--) {
        if (state.messages[i].role === 'USER') {
          content = state.messages[i].content;
          break;
        }
      }
      if (content) send(content);
    },
    [state.messages, send]
  );

  const newChat = useCallback(() => {
    conversationGenerationRef.current += 1;
    ensureConversationPromiseRef.current = null;
    ensureConversationRequestRef.current = null;
    setListView(false);
    setError(null);
    setConversationId(null);
    conversationIdRef.current = null;
    lastSeqRef.current = 0;
    dispatch({ t: 'reset' });
  }, []);

  const openConversation = useCallback(
    (convId: string) => {
      conversationGenerationRef.current += 1;
      ensureConversationPromiseRef.current = null;
      ensureConversationRequestRef.current = null;
      setListView(false);
      setError(null);
      setConversationId(convId);
      conversationIdRef.current = convId;
      lastSeqRef.current = 0;
      dispatch({ t: 'reset' });
      // GET messages clears has_unread server-side; refresh the list afterwards.
      refetchMessages(convId).then(() =>
        setConversationsRefreshKey((k) => k + 1)
      );
    },
    [refetchMessages]
  );

  const showList = useCallback((show: boolean) => setListView(show), []);

  return useMemo(
    () => ({
      open,
      setOpen,
      listView,
      showList,
      conversationId,
      messages: state.messages,
      turnActive: state.turnActive,
      connected,
      reconnecting,
      banner,
      loadingHistory,
      fullScreen,
      toggleFullScreen,
      pinned,
      togglePinned,
      width: widthState,
      setWidth,
      resizing,
      setResizing,
      send,
      answerQuestion,
      decideAction,
      retryTurn,
      newChat,
      openConversation,
      conversationsRefreshKey,
      forbidden,
      error,
      dismissError,
      reportError,
      restoredDraft,
    }),
    [
      open,
      setOpen,
      listView,
      showList,
      conversationId,
      state.messages,
      state.turnActive,
      connected,
      reconnecting,
      banner,
      loadingHistory,
      fullScreen,
      toggleFullScreen,
      pinned,
      togglePinned,
      widthState,
      setWidth,
      resizing,
      send,
      answerQuestion,
      decideAction,
      retryTurn,
      newChat,
      openConversation,
      conversationsRefreshKey,
      forbidden,
      error,
      dismissError,
      reportError,
      restoredDraft,
    ]
  );
}
